import React from 'react';
import { formatCityDisplay } from '../utils/helpers';

const ClinicCard = ({ clinic, featured = false, showGoogleReviews = false, onViewClinic }) => (
  <div className="relative bg-white rounded-2xl shadow-lg hover:shadow-xl overflow-hidden transition-all duration-300 hover:-translate-y-1 transform flex flex-col h-full">
    {/* Gradient Header - exactly like reference */}
    <div className="h-3 bg-gradient-to-r from-blue-500 to-green-500 flex-shrink-0"></div>
    
    {/* Featured Badge - positioned like reference */}
    {featured && (
      <div className="absolute top-6 right-6 z-10">
        <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-yellow-400 text-yellow-900">
          <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
            <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
          </svg>
          Kiemelt
        </span>
      </div>
    )}

    {/* Content Section - matching reference layout with flex-grow */}
    <div className="p-6 flex flex-col flex-grow">
      {/* Top content that can vary in height */}
      <div className="flex-grow">
        {/* Clinic Name */}
        <h3 className="text-2xl font-bold text-gray-900 mb-3">{clinic.name}</h3>
        
        {/* Location with gray icon like reference */}
        <div className="flex items-center text-gray-600 mb-6">
          <svg className="w-5 h-5 mr-2 text-gray-500" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
          </svg>
          <span className="text-lg">{formatCityDisplay(clinic.city, clinic.district)}</span>
        </div>

        {/* Description/Services area */}
        <div className="mb-6">
          <p className="text-gray-600 text-base leading-relaxed mb-4">
            Modern fogorvosi klinika a belvárosban, ahol a legújabb technológiával és szakértő csapatunkkal...
          </p>
          
          {/* Services - simple gray bordered chips like reference */}
          <div className="flex flex-wrap gap-2">
            {clinic.services.slice(0, 3).map((service, index) => (
              <span key={index} className="px-3 py-1 rounded-full text-sm text-gray-700 border border-gray-300 bg-white">
                {service}
              </span>
            ))}
            {clinic.services.length > 3 && (
              <span className="px-3 py-1 rounded-full text-sm text-gray-500 border border-gray-300 bg-gray-50">
                +{clinic.services.length - 3} további
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Bottom section with phone/reviews and button - always at the bottom */}
      <div className="flex items-center justify-between pt-4 mt-auto">
        {/* Show Google Reviews or Phone based on showGoogleReviews prop */}
        {showGoogleReviews && clinic.google_rating && clinic.google_review_count > 0 ? (
          <div className="flex items-center text-gray-600">
            <div className="flex items-center">
              <span className="text-lg font-bold text-gray-900 mr-2">{clinic.google_rating.toFixed(1)}</span>
              <div className="flex mr-2">
                {[1, 2, 3, 4, 5].map((star) => (
                  <svg 
                    key={star} 
                    className={`w-4 h-4 ${star <= Math.round(clinic.google_rating) ? 'text-yellow-400' : 'text-gray-300'}`}
                    fill="currentColor" 
                    viewBox="0 0 20 20"
                  >
                    <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                  </svg>
                ))}
              </div>
              <a 
                href={clinic.google_business_link} 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-blue-600 hover:text-blue-800 font-medium underline"
                onClick={(e) => e.stopPropagation()}
              >
                ({clinic.google_review_count})
              </a>
            </div>
          </div>
        ) : (
          <div className="flex items-center text-gray-600">
            <svg className="w-4 h-4 mr-2 text-gray-500" fill="currentColor" viewBox="0 0 20 20">
              <path d="M2 3a1 1 0 011-1h2.153a1 1 0 01.986.836l.74 4.435a1 1 0 01-.54 1.06l-1.548.773a11.037 11.037 0 006.105 6.105l.774-1.548a1 1 0 011.059-.54l4.435.74a1 1 0 01.836.986V17a1 1 0 01-1 1h-2C7.82 18 2 12.18 2 5V3z" />
            </svg>
            <span className="font-medium text-lg">{clinic.phone}</span>
          </div>
        )}
        
        {/* Button on right like reference */}
        <button 
          onClick={() => onViewClinic(clinic.id)}
          className="bg-gradient-to-r from-blue-600 to-green-600 text-white py-3 px-6 rounded-xl font-semibold hover:from-blue-700 hover:to-green-700 transition-all duration-300 flex items-center space-x-2 flex-shrink-0"
        >
          <span>Részletek</span>
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 7l5 5m0 0l-5 5m5-5H6" />
          </svg>
        </button>
      </div>
    </div>
  </div>
);

export default ClinicCard;
